import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, Download, Share2, ArrowLeft, Check, Printer, Mail, MessageCircle, Facebook, Smartphone } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const SHARE_URL = "https://reunion-discover.emergent.host";
const TAGLINE = "Découvre les meilleurs spots de l'île de la Réunion 🌴";

export default function Share() {
  const canvasWrapperRef = useRef(null);
  const [size, setSize] = useState(280);
  const [variant, setVariant] = useState("sunset"); // sunset, lagoon, dark

  const palette = {
    sunset: { fg: "#E84A1F", bg: "#FFFBF5", grad: "from-[#FF6B35] to-[#E84A1F]" },
    lagoon: { fg: "#00A6B3", bg: "#FFFBF5", grad: "from-[#00A6B3] to-[#2EC4B6]" },
    dark: { fg: "#FFFFFF", bg: "#1A1A1A", grad: "from-[#1A1A1A] to-[#2A2A2A]" },
  }[variant];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      toast.success("Lien copié !");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const downloadPng = () => {
    const canvas = canvasWrapperRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "runspot-qr.png";
    a.click();
    toast.success("QR code téléchargé");
  };

  const printQR = () => window.print();

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "RunSpot — Île de la Réunion", text: TAGLINE, url: SHARE_URL });
      } catch {}
    } else copyLink();
  };

  const links = [
    { label: "WhatsApp", icon: MessageCircle, color: "#25D366",
      url: `https://wa.me/?text=${encodeURIComponent(TAGLINE + " " + SHARE_URL)}` },
    { label: "Facebook", icon: Facebook, color: "#1877F2",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}` },
    { label: "Email", icon: Mail, color: "#FF6B35",
      url: `mailto:?subject=${encodeURIComponent("Découvre RunSpot")}&body=${encodeURIComponent(TAGLINE + "\n\n" + SHARE_URL)}` },
    { label: "SMS", icon: Smartphone, color: "#00A6B3",
      url: `sms:?body=${encodeURIComponent(TAGLINE + " " + SHARE_URL)}` },
  ];

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="share-page">
      <style>{`@media print {
        body * { visibility: hidden; }
        #print-area, #print-area * { visibility: visible; }
        #print-area { position: absolute; left: 0; top: 0; width: 100%; }
      }`}</style>

      <div className="pt-24 sm:pt-28 pb-16 max-w-5xl mx-auto px-4 sm:px-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-[#1A1A1A]/60 hover:text-[#FF6B35] mb-6" data-testid="share-back">
          <ArrowLeft size={16}/> Retour
        </Link>

        <div className="mb-10">
          <div className="uppercase-label text-[#FF6B35]">Partage RunSpot</div>
          <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tighter text-[#1A1A1A] mt-1">
            Faire <span className="text-[#FF6B35]">connaître</span> RunSpot
          </h1>
          <p className="text-[#1A1A1A]/60 mt-2 max-w-xl">
            Imprime le QR code et colle-le chez tes commerçants partenaires, ou partage le lien direct sur tes réseaux.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* QR card (printable) */}
          <motion.div
            id="print-area"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className={`lg:col-span-3 rounded-3xl p-8 sm:p-12 bg-gradient-to-br ${palette.grad} shadow-xl text-center relative overflow-hidden`}
          >
            <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/15 blur-3xl"/>
            <div className="absolute -bottom-10 -left-10 w-56 h-56 rounded-full bg-white/10 blur-3xl"/>
            <div className="relative">
              <div className="uppercase-label text-white/85">Île de la Réunion · 974</div>
              <div className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1 text-white text-shadow-soft">
                Run<span className="opacity-90">Spot</span>
              </div>
              <p className="text-white/85 text-sm mt-2 max-w-xs mx-auto">
                Scanne pour découvrir les meilleurs spots péi.
              </p>

              <div ref={canvasWrapperRef} className="mt-6 inline-block bg-white p-5 rounded-3xl shadow-2xl">
                <QRCodeCanvas
                  value={SHARE_URL}
                  size={size}
                  level="H"
                  fgColor={palette.fg}
                  bgColor={palette.bg}
                  marginSize={2}
                  imageSettings={{
                    src: "/icon-192.png",
                    height: 56,
                    width: 56,
                    excavate: true,
                  }}
                />
              </div>

              <div className="mt-5 text-white text-sm font-medium opacity-95 break-all">
                reunion-discover.emergent.host
              </div>
              <div className="mt-1 text-xs uppercase tracking-[0.25em] text-white/70">
                Restos · Plages · Nightlife · Events
              </div>
            </div>
          </motion.div>

          {/* Controls */}
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-3xl bg-white border border-black/5 p-6 shadow-sm">
              <div className="uppercase-label text-[#1A1A1A]/60">Style</div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {[
                  { id: "sunset", label: "Sunset", color: "#FF6B35" },
                  { id: "lagoon", label: "Lagon", color: "#00A6B3" },
                  { id: "dark", label: "Volcan", color: "#1A1A1A" },
                ].map((p) => (
                  <button
                    key={p.id}
                    data-testid={`variant-${p.id}`}
                    onClick={() => setVariant(p.id)}
                    className={`rounded-xl py-2.5 text-xs font-bold transition-all border-2 ${
                      variant === p.id ? "border-[#FF6B35] shadow-md" : "border-transparent"
                    }`}
                    style={{ background: p.color, color: "#fff" }}
                  >
                    {variant === p.id && <Check size={12} className="inline mr-1"/>}
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="uppercase-label text-[#1A1A1A]/60 mt-5">Taille</div>
              <input type="range" min="180" max="400" value={size} onChange={(e)=>setSize(Number(e.target.value))}
                data-testid="size-slider"
                className="w-full mt-2 accent-[#FF6B35]"/>
              <div className="text-xs text-[#1A1A1A]/50">{size}×{size}px</div>
            </div>

            <div className="rounded-3xl bg-white border border-black/5 p-6 shadow-sm">
              <div className="uppercase-label text-[#1A1A1A]/60">Actions</div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <Button data-testid="download-btn" onClick={downloadPng}
                  className="rounded-full bg-gradient-to-r from-[#FF6B35] to-[#E84A1F] hover:opacity-90 text-white font-bold py-5 shadow-md shadow-[#FF6B35]/30">
                  <Download size={14} className="mr-1.5"/> PNG
                </Button>
                <Button data-testid="print-btn" onClick={printQR} variant="outline"
                  className="rounded-full font-bold py-5 border-[#1A1A1A]/10">
                  <Printer size={14} className="mr-1.5"/> Imprimer
                </Button>
                <Button data-testid="copy-link-btn" onClick={copyLink} variant="outline"
                  className="rounded-full font-bold py-5 border-[#1A1A1A]/10">
                  <Copy size={14} className="mr-1.5"/> Copier
                </Button>
                <Button data-testid="native-share-btn" onClick={nativeShare}
                  className="rounded-full bg-[#1A1A1A] hover:bg-[#000] text-white font-bold py-5">
                  <Share2 size={14} className="mr-1.5"/> Partager
                </Button>
              </div>
            </div>

            <div className="rounded-3xl bg-white border border-black/5 p-6 shadow-sm">
              <div className="uppercase-label text-[#1A1A1A]/60">Réseaux</div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                {links.map((l) => {
                  const Icon = l.icon;
                  return (
                    <a key={l.label} href={l.url} target="_blank" rel="noreferrer"
                      data-testid={`share-${l.label.toLowerCase()}`}
                      className="rounded-xl border border-black/5 px-4 py-3 flex items-center gap-2 text-sm font-bold hover:bg-[#FAF1E1] transition-colors"
                      style={{ color: l.color }}
                    >
                      <Icon size={16}/> {l.label}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-12 rounded-3xl bg-[#FAF1E1] p-6 sm:p-8 border border-black/5">
          <div className="uppercase-label text-[#FF6B35]">Astuce growth</div>
          <h3 className="font-display font-black text-xl sm:text-2xl tracking-tighter mt-1">
            3 idées pour faire connaître RunSpot
          </h3>
          <ul className="mt-4 space-y-2.5 text-sm text-[#1A1A1A]/75">
            <li className="flex gap-3"><span className="font-bold text-[#FF6B35]">①</span> Imprime le QR en format A5 et propose aux commerçants de l'afficher en vitrine.</li>
            <li className="flex gap-3"><span className="font-bold text-[#FF6B35]">②</span> Partage le lien sur ton compte Instagram avec un visuel coucher de soleil péi.</li>
            <li className="flex gap-3"><span className="font-bold text-[#FF6B35]">③</span> Glisse-le dans les groupes WhatsApp locaux et marchés du week-end.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
